import { twMerge } from 'tailwind-merge';

import type { NormalizedBarChartData } from '../shared/types';
import type { SeriesSignSummary } from '../shared/chart-helpers';

import { colorForSeries } from '../shared/chart-palette';

export interface BarChartLegendProps {
  series: NormalizedBarChartData['series'];
  signSummary: SeriesSignSummary;
  className?: string;
}

interface LegendGroupProps {
  series: NormalizedBarChartData['series'];
  isNegative: boolean;
  ariaLabel: string;
}

const LegendGroup = ({ series, isNegative, ariaLabel }: LegendGroupProps) => (
  // eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari VoiceOver strips list role with list-none
  <ul
    role="list"
    aria-label={ariaLabel}
    className="flex flex-wrap items-center gap-2.5 m-0 p-0 list-none font-text text-xs text-gray-500"
  >
    {series.map((singleSeries, seriesIndex) => (
      <li
        key={`${seriesIndex}-${singleSeries.label}`}
        className="flex items-center gap-1"
      >
        <span
          aria-hidden="true"
          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
          style={{
            backgroundColor: colorForSeries(
              seriesIndex,
              series.length,
              isNegative
            ),
          }}
        />
        {singleSeries.label}
      </li>
    ))}
  </ul>
);

export const BarChartLegend = ({
  series,
  signSummary,
  className,
}: BarChartLegendProps) => {
  if (series.length <= 1) return null;
  if (!signSummary.hasPositive && !signSummary.hasNegative) return null;

  return (
    <div className={twMerge('flex flex-wrap items-center gap-10', className)}>
      {signSummary.hasPositive && (
        <LegendGroup
          series={series}
          isNegative={false}
          ariaLabel="Positive values"
        />
      )}
      {signSummary.hasNegative && (
        <LegendGroup
          series={series}
          isNegative={true}
          ariaLabel="Negative values"
        />
      )}
    </div>
  );
};
